/**
 * Customers — list.
 *
 * Post-showcase changes (E11 / dealer lens):
 *   - "Risk · Next action" no longer shows a free-text string. It shows the
 *     real next event (earliest open task) via one batch call, red when
 *     overdue, and a warning when a customer has a live lead and nothing
 *     scheduled. Sortable by due date.
 *   - Risk is multi-select; a NEEDS ATTENTION toggle surfaces the customers a
 *     manager opens this page for: live lead + no next step, or overdue.
 *   - Paged. The CRM context loads the first 100 customers; the table pages
 *     them 25 at a time and says so when it is at the cap, rather than
 *     rendering an unbounded list. Server-side paging of the context itself
 *     is a CrmProvider change (see notes).
 *   - Export CSV is real: it exports the filtered set.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { downloadCsv, toCsv } from '../lib/download';
import TopBar from '../components/TopBar';
import CustomerModal from '../components/CustomerModal';
import MultiSelectFilter from '../components/MultiSelectFilter';
import NextEventChip from '../components/NextEventChip';
import NextEventModal from '../components/NextEventModal';
import OwnershipChip from '../components/OwnershipChip';
import { useCrm, isClosedStage } from '../lib/crm';
import { useConsultantOptions } from '../lib/useDirectory';
import { useToast } from '../lib/toast';
import { customersService } from '../lib/services';
import { relativeDue } from '../lib/datetime';
import { fmtMoney } from '../lib/deals';

const RISK_OPTIONS = [
  { value: 'LOW',  label: 'Low',  color: 'var(--success)' },
  { value: 'MED',  label: 'Med',  color: 'var(--warning)' },
  { value: 'HIGH', label: 'High', color: 'var(--danger)' },
];
const PAGE_SIZE = 25;
// Columns: avatar, customer, contact, tags, stage, LTV, risk/next event, consultant + handler, actions.
const ROW_GRID = '36px 1.5fr 1.3fr 0.9fr 0.8fr 0.7fr 1.3fr 1.3fr 1fr';
const HOT_SCORE = 80; // same threshold the Lead Accelerator and Dashboard use for a hot lead

const initialsOf = (name = '') =>
  name.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?';

export default function Customers() {
  const { customers, customersTotal, leads, addCustomer, updateCustomer } = useCrm();
  const consultants = useConsultantOptions();
  const consultantName = useMemo(() => {
    const m = new Map(consultants.options.map((o) => [o.id, o.primary]));
    return (id) => (id ? m.get(id) || null : null);
  }, [consultants.options]);

  // Each customer's live leads, best-scored first. The consultant and the
  // AI/Human conversation handler live on the lead, not the customer, so the
  // list shows the primary live lead's and counts the rest.
  const liveLeadsByCustomer = useMemo(() => {
    const m = new Map();
    for (const l of leads) {
      if (!l.customerId || isClosedStage(l.stage)) continue;
      if (!m.has(l.customerId)) m.set(l.customerId, []);
      m.get(l.customerId).push(l);
    }
    m.forEach((list) => list.sort((a, b) => (b.score || 0) - (a.score || 0)));
    return m;
  }, [leads]);
  const hotLeadCount = useMemo(
    () => leads.filter((l) => !isClosedStage(l.stage) && (l.score || 0) >= HOT_SCORE).length,
    [leads],
  );
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState('');
  // Deep link from the dashboard's At-Risk Customers tile: /customers?risk=HIGH
  // opens the grid pre-filtered to that risk tier (comma-separated for several).
  const [risks, setRisks] = useState(() => {
    const r = searchParams.get('risk');
    if (!r) return new Set();
    const valid = new Set(['LOW', 'MED', 'HIGH']);
    return new Set(r.split(',').map((s) => s.trim().toUpperCase()).filter((s) => valid.has(s)));
  });
  const [attention, setAttention] = useState(false);
  // 'all' | 'incomplete' — isolate records with no name, email or phone so they
  // can be found and fixed rather than silently padding the list.
  const [completeness, setCompleteness] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState({ open: false, mode: 'create', customer: null });

  // Next event + open-lead count per customer, one batch call.
  const [next, setNext] = useState({});
  const [nextLoaded, setNextLoaded] = useState(false);
  const [followUpFor, setFollowUpFor] = useState(null); // customer getting a next step from the list
  const [nextRefresh, setNextRefresh] = useState(0);    // bump to re-read next events after a save
  useEffect(() => {
    let cancelled = false;
    const ids = customers.map((c) => c.id).filter(Boolean);
    if (!ids.length) { setNext({}); setNextLoaded(true); return undefined; }
    customersService.nextEvents(ids)
      .then((map) => { if (!cancelled) { setNext(map || {}); setNextLoaded(true); } })
      .catch(() => { if (!cancelled) setNextLoaded(false); });
    return () => { cancelled = true; };
  }, [customers, nextRefresh]);

  const openCreate = () => setModal({ open: true, mode: 'create', customer: null });
  const openEdit   = (c) => setModal({ open: true, mode: 'edit', customer: c });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  // Deep link from the dashboard's "New customer" quick action: /customers?new=1
  // auto-opens the create dialog, then the param is cleared so a refresh or
  // back-nav doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreate();
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSave = async (payload, mode) => {
    if (mode === 'edit') {
      await updateCustomer(modal.customer.id, payload);
      toast(`Customer "${payload.name || modal.customer.name}" updated`, { variant: 'success' });
    } else {
      const saved = await addCustomer(payload);
      toast(`Customer "${saved?.name || payload.name}" added`, { variant: 'success' });
    }
  };

  const needsAttention = (c) => {
    const n = next[c.id];
    if (!n) return false;
    if ((n.openLeads ?? 0) === 0) return false;
    if (!n.nextEvent) return true;
    return !!(n.nextEvent.overdue ?? relativeDue(n.nextEvent.dueAt).overdue);
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = customers.filter((c) => {
      if (risks.size && !risks.has(c.churnRisk)) return false;
      if (attention && !needsAttention(c)) return false;
      if (completeness === 'incomplete' && c.identified) return false;
      if (!term) return true;
      return (
        (c.name || '').toLowerCase().includes(term)
        || (c.email || '').toLowerCase().includes(term)
        || (c.phone || '').toLowerCase().includes(term)
        || (c.tags || []).join(' ').toLowerCase().includes(term)
      );
    });

    out = [...out].sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (sort.key === 'nextDue') {
        // Overdue first, then soonest; customers with no next event last.
        const ad = next[a.id]?.nextEvent?.dueAt, bd = next[b.id]?.nextEvent?.dueAt;
        const av = ad ? new Date(ad).getTime() : Number.MAX_SAFE_INTEGER;
        const bv = bd ? new Date(bd).getTime() : Number.MAX_SAFE_INTEGER;
        return (av - bv) * dir;
      }
      if (sort.key === 'name') {
        // Records with no name, email or phone sort last whichever direction is
        // chosen. Alphabetically they would otherwise cluster at one end and
        // bury every real customer behind a wall of placeholders.
        if (a.identified !== b.identified) return a.identified ? -1 : 1;
      }
      const ax = a[sort.key], bx = b[sort.key];
      if (typeof ax === 'number' && typeof bx === 'number') return (ax - bx) * dir;
      return String(ax || '').localeCompare(String(bx || '')) * dir;
    });
    return out;
    // needsAttention closes over `next`; listed explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, q, risks, attention, completeness, sort, next]);

  // Reset to the first page whenever the filter set changes.
  useEffect(() => { setPage(0); }, [q, risks, attention, completeness, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const attentionCount = useMemo(
    () => (nextLoaded ? customers.filter(needsAttention).length : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers, next, nextLoaded],
  );

  const incompleteCount = useMemo(
    () => customers.filter((c) => !c.identified).length,
    [customers],
  );

  const totals = useMemo(() => ({
    ltv: customers.reduce((sum, c) => sum + (c.ltv || 0), 0),
    atRisk: customers.filter((c) => c.churnRisk === 'HIGH').length,
  }), [customers]);

  // The server knows how many customers exist; this page only holds what was loaded.
  const notAllLoaded = customersTotal > customers.length;
  // Agent Test Console customers: listed here (operational view) but not counted
  // by the Dashboard (reporting), which is why the two totals can differ.
  const testCount = useMemo(() => customers.filter((c) => c.simulated).length, [customers]);

  const exportCsv = () => {
    if (!filtered.length) { toast('No customers to export', { variant: 'info' }); return; }
    const head = ['Name', 'Phone', 'Email', 'Tags', 'Stage', 'LTV', 'Risk', 'Next event', 'Next due',
      'Assigned consultant', 'Conversation handler'];
    const rows = filtered.map((c) => {
      const n = next[c.id]?.nextEvent;
      const lead = liveLeadsByCustomer.get(c.id)?.[0];
      return [c.name, c.phone, c.email, (c.tags || []).join('; '), c.stage, c.ltv ?? '', c.churnRisk,
        n?.title ?? '', n?.dueAt ?? '',
        lead ? (consultantName(lead.assignedConsultantId) || 'Unassigned') : '',
        lead ? ((lead.assistedBy || 'AI').toUpperCase() === 'HUMAN' ? 'Human' : 'AI') : ''];
    });
    downloadCsv(`customers-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(head, rows));
    toast(
      `Exported ${filtered.length} customer${filtered.length === 1 ? '' : 's'}`
        + (notAllLoaded ? ` (of ${customers.length} loaded; ${customersTotal} in total)` : ''),
      { variant: 'success' },
    );
  };

  const anyFilter = q.trim() || risks.size > 0 || attention;

  return (
    <>
      <TopBar
        right={
          <>
            <button type="button" className="btn small" onClick={exportCsv} disabled={!filtered.length}>Export CSV</button>
            <button type="button" className="btn small primary" onClick={openCreate}>+ New customer</button>
          </>
        }
      />
      <div className="app-content">
        {/* KPI strip */}
        <div className="grid-4" style={{ marginBottom: 14 }}>
          <div className="card">
            <div className="card-label">CUSTOMERS</div>
            <div className="metric-value" style={{ fontSize: 26 }}>{customersTotal.toLocaleString()}</div>
            <div className="mono text-xs text-muted">
              {notAllLoaded ? `${customers.length.toLocaleString()} of ${customersTotal.toLocaleString()} loaded` : 'total in CRM'}
              {testCount > 0 && ` · incl. ${testCount} test`}
            </div>
            {testCount > 0 && (
              <div className="mono text-xs text-muted" title="Agent Test Console customers are listed here but not counted on the Dashboard">
                Dashboard counts {(customersTotal - testCount).toLocaleString()}
              </div>
            )}
          </div>
          <div className="card">
            <div className="card-label">NEEDS ATTENTION</div>
            <div className="metric-value" style={{ fontSize: 26, color: attentionCount > 0 ? 'var(--warning)' : 'var(--success)' }}>
              {attentionCount ?? '—'}
            </div>
            <div className="mono text-xs text-muted">customers with a live lead · no next step or overdue</div>
          </div>
          <div className="card">
            <div className="card-label">AT RISK</div>
            <div className="metric-value" style={{ fontSize: 26, color: 'var(--danger)' }}>{totals.atRisk}</div>
            <div className="mono text-xs text-muted">customers · no contact for 60+ days</div>
          </div>
          <div className="card">
            <div className="card-label">HOT LEADS</div>
            <div className="metric-value" style={{ fontSize: 26, color: 'var(--warning)' }}>{hotLeadCount}</div>
            <div className="mono text-xs text-muted">open leads scoring {HOT_SCORE}+</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="crm-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div className="crm-search">
            <span style={{ fontSize: 13 }}>🔍</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone, tag…"
              aria-label="Search customers"
            />
          </div>
          <MultiSelectFilter label="RISK" options={RISK_OPTIONS} value={risks} onChange={setRisks} />
          <button
            type="button"
            className={`pill${attention ? ' active' : ''}`}
            aria-pressed={attention}
            onClick={() => setAttention((v) => !v)}
            disabled={!nextLoaded}
            title={nextLoaded ? 'Live lead with no next step, or an overdue one' : 'Next-event data not available yet'}
            style={!attention && attentionCount > 0 ? { borderColor: 'var(--warning)', color: 'var(--warning)' } : undefined}
          >
            ⚠ Needs attention{attentionCount > 0 ? ` · ${attentionCount}` : ''}
          </button>
          {incompleteCount > 0 && (
            <button
              type="button"
              className={`pill${completeness === 'incomplete' ? ' active' : ''}`}
              aria-pressed={completeness === 'incomplete'}
              onClick={() => setCompleteness((v) => (v === 'incomplete' ? 'all' : 'incomplete'))}
              title="Customers with no name, email or phone — usually created by an inbound channel before the conversation gave one"
            >
              Incomplete · {incompleteCount}
            </button>
          )}
          {(anyFilter || completeness !== 'all') && (
            <button type="button" className="btn small" onClick={() => { setQ(''); setRisks(new Set()); setAttention(false); setCompleteness('all'); }}>
              Clear
            </button>
          )}
          <span style={{ marginLeft: 'auto' }} className="mono text-xs text-muted">
            {filtered.length} of {customers.length}{notAllLoaded ? ` loaded (${customersTotal} total)` : ''}
          </span>
        </div>

        {/* Table — overflowX auto so narrow windows can scroll the columns
            instead of clipping them. */}
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <div className="crm-row head" style={{ gridTemplateColumns: ROW_GRID }}>
            <span />
            <span onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Customer {sort.key === 'name' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
            <span>Contact</span>
            <span>Tags</span>
            <span title="The customer's lifecycle state. It is not the stage of their leads — open leads and their stage are shown under the name and in the Lead Accelerator.">State</span>
            <span onClick={() => toggleSort('ltv')} style={{ cursor: 'pointer', textAlign: 'right' }}>LTV {sort.key === 'ltv' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
            <span onClick={() => nextLoaded && toggleSort('nextDue')} style={{ cursor: nextLoaded ? 'pointer' : 'default' }}>
              Risk · Next event {sort.key === 'nextDue' ? (sort.dir === 'asc' ? '▲' : '▼') : (nextLoaded ? '⇅' : '')}
            </span>
            <span title="Consultant on the customer's live lead, and whether the AI or a person is handling the conversation">
              Consultant · Handler
            </span>
            <span style={{ textAlign: 'right' }}>Open</span>
          </div>

          {filtered.length === 0 ? (
            customers.length === 0 ? (
              <div className="empty-state">
                <div className="icon">👤</div>
                <div className="title">No customers yet</div>
                <div className="desc">Add your first prospect or owner — you can enrich the record with tags, VIP status, or a fleet account afterwards.</div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="icon">🔍</div>
                <div className="title">No customers match</div>
                <div className="desc">
                  {attention ? 'Nobody needs attention right now — every customer with a live lead has a next step.'
                    : q.trim() ? `Nothing matches "${q.trim()}".` : 'No customers in the selected risk tiers.'}
                </div>
                <button type="button" className="btn small" style={{ marginTop: 8 }} onClick={() => { setQ(''); setRisks(new Set()); setAttention(false); }}>
                  Clear search & filters
                </button>
              </div>
            )
          ) : (
            pageRows.map((c) => {
              const n = next[c.id];
              const flag = needsAttention(c);
              const live = liveLeadsByCustomer.get(c.id) || [];
              const primaryLead = live[0] || null;
              return (
                <div key={c.id} className="crm-row" style={{ position: 'relative', gridTemplateColumns: ROW_GRID, background: flag ? 'color-mix(in srgb, var(--warning) 6%, transparent)' : undefined }}>
                  {/* Card-wide click target: routes to the detail page. Sits
                      below the Edit button so the button stays clickable. */}
                  <Link to={`/customers/${c.id}`} aria-label={`Open ${c.displayName}`} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
                  <span
                    className="avatar-pill"
                    style={{ position: 'relative', zIndex: 1, opacity: c.identified ? 1 : 0.45 }}
                  >
                    {c.initials || initialsOf(c.name)}
                  </span>
                  <span style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontWeight: c.hasRealName ? 600 : 500, color: c.hasRealName ? undefined : 'var(--text-muted)', fontStyle: c.hasRealName ? undefined : 'italic' }}>
                      {c.displayName}
                      {c.simulated && (
                        <span className="pill" style={{ marginLeft: 6, fontSize: 10 }} title="Created by the Agent Test Console; not counted on the Dashboard">Test</span>
                      )}
                    </div>
                    <div className="mono text-xs text-muted">
                      {c.since ? `since ${c.since}` : ''}
                      {n?.openLeads > 0 ? `${c.since ? ' · ' : ''}${n.openLeads} open lead${n.openLeads === 1 ? '' : 's'}` : ''}
                    </div>
                    {/* Marker: this customer has an open lead with no activity for
                        3+ days. Separate from the risk pill, which is about the
                        customer (no contact for 60+ days) — a customer can carry both. */}
                    {n?.stalledLeads > 0 && (
                      <div
                        className="mono text-xs"
                        style={{ color: 'var(--warning)', marginTop: 2 }}
                        title="An open lead for this customer has had no activity for 3+ days"
                      >
                        ⏸ {n.stalledLeads} stalled lead{n.stalledLeads === 1 ? '' : 's'}
                      </div>
                    )}
                  </span>
                  <span className="mono text-xs text-muted" style={{ lineHeight: 1.5, position: 'relative', zIndex: 1 }}>
                    {c.phone || c.email ? (
                      <>
                        {c.phone}{c.phone && c.email ? <br /> : null}{c.email}
                      </>
                    ) : (
                      /* An empty record is a data problem, not a rendering one —
                         say so plainly instead of printing dashes in every column. */
                      <span style={{ fontStyle: 'italic' }}>No contact details</span>
                    )}
                  </span>
                  <span style={{ overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                    {(c.tags || []).length
                      ? c.tags.slice(0, 3).map((t) => <span key={t} className="tag-chip">{t}</span>)
                      : <span className="text-muted" title="No tags on this customer">—</span>}
                  </span>
                  <span className="mono text-xs text-muted" style={{ position: 'relative', zIndex: 1 }}>{c.stage}</span>
                  <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', position: 'relative', zIndex: 1 }}>
                    {c.ltv ? fmtMoney(c.ltv) : '—'}
                  </span>
                  <span style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
                    <span className={`risk-pill risk-${c.churnRisk}`}>{c.churnRisk}</span>
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      {!nextLoaded ? (
                        <span className="mono text-xs text-muted">{c.nextAction || '—'}</span>
                      ) : (n?.openLeads ?? 0) > 0 || n?.nextEvent ? (
                        <NextEventChip
                          nextEvent={n?.nextEvent || null}
                          compact
                          // A live lead with nothing scheduled: set the follow-up right here.
                          onSet={(e) => { e.preventDefault(); e.stopPropagation(); setFollowUpFor(c); }}
                        />
                      ) : (
                        <span className="mono text-xs text-muted">no live lead</span>
                      )}
                    </div>
                  </span>
                  <span style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
                    {primaryLead ? (
                      <>
                        <div style={{ fontSize: 13 }}>
                          {consultantName(primaryLead.assignedConsultantId)
                            || (primaryLead.assignedConsultantId ? '…' : <span className="text-muted">Unassigned</span>)}
                          {live.length > 1 && (
                            <span className="mono text-xs text-muted" title={`${live.length} live leads; showing the highest-scored`}>
                              {' '}+{live.length - 1} more
                            </span>
                          )}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <OwnershipChip lead={primaryLead} compact />
                        </div>
                      </>
                    ) : (
                      <span className="mono text-xs text-muted">no live lead</span>
                    )}
                  </span>
                  <span style={{ textAlign: 'right', position: 'relative', zIndex: 2, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn small"
                      aria-label={`Edit ${c.name}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(c); }}
                    >
                      ✎ Edit
                    </button>
                    <Link to={`/customers/${c.id}`} className="pill accent filled" style={{ cursor: 'pointer', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                      Open →
                    </Link>
                  </span>
                </div>
              );
            })
          )}

          {filtered.length > PAGE_SIZE && (
            <div className="flex-between" style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>
              <span className="mono text-xs text-muted">
                {page * PAGE_SIZE + 1}–{Math.min(filtered.length, (page + 1) * PAGE_SIZE)} of {filtered.length}
              </span>
              <div className="flex-row" style={{ gap: 4 }}>
                <button type="button" className="btn small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                <span className="mono text-xs text-muted" style={{ padding: '0 8px', alignSelf: 'center' }}>{page + 1} / {pageCount}</span>
                <button type="button" className="btn small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>Next ›</button>
              </div>
            </div>
          )}
        </div>

        {notAllLoaded && (
          <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>
            {customers.length.toLocaleString()} of {customersTotal.toLocaleString()} customers are loaded. Search, filters, the
            KPI tiles and CSV export cover the loaded customers only.
          </div>
        )}
      </div>

      <NextEventModal
        open={!!followUpFor}
        // The next step belongs to the customer's live lead when there is one.
        leadId={followUpFor ? (liveLeadsByCustomer.get(followUpFor.id)?.[0]?.id || null) : null}
        customerId={followUpFor && !liveLeadsByCustomer.get(followUpFor.id)?.length ? followUpFor.id : null}
        defaultAssigneeId={followUpFor ? (liveLeadsByCustomer.get(followUpFor.id)?.[0]?.assignedConsultantId || null) : null}
        onClose={() => setFollowUpFor(null)}
        onSaved={() => setNextRefresh((n) => n + 1)}
      />

      <CustomerModal
        open={modal.open}
        mode={modal.mode}
        customer={modal.customer}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  );
}
